import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Play, 
  Pause, 
  Download, 
  CheckCircle, 
  AlertCircle,
  ArrowRight,
  RefreshCw,
  FileText,
  Video,
  Music
} from 'lucide-react';

function WorkflowHub() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [article, setArticle] = useState(null);
  const [workflowStatus, setWorkflowStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatingAudio, setGeneratingAudio] = useState({});
  const [playingAudio, setPlayingAudio] = useState(null);

  const fetchWorkflowStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/workflow/${id}`);
      if (!response.ok) throw new Error('Failed to fetch workflow status');
      
      const data = await response.json();
      setArticle(data.article);
      setWorkflowStatus(data.workflow_status);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchWorkflowStatus();
    }
  }, [id, fetchWorkflowStatus]);

  const generateAudio = async (scriptType) => {
    try {
      setGeneratingAudio(prev => ({ ...prev, [scriptType]: true }));
      
      const formData = new FormData();
      formData.append('script_type', scriptType);
      
      const response = await fetch(`/api/articles/${id}/audio/generate`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) throw new Error('Failed to generate audio');
      
      await response.json();
      
      // Refresh workflow status
      await fetchWorkflowStatus();
      
    } catch (err) {
      setError(err.message);
    } finally {
      setGeneratingAudio(prev => ({ ...prev, [scriptType]: false }));
    }
  };

  const approveAudio = async (audioId) => {
    try {
      const response = await fetch(`/api/audio/${audioId}/approve`, {
        method: 'POST'
      });
      
      if (!response.ok) throw new Error('Failed to approve audio');
      
      // Refresh workflow status
      await fetchWorkflowStatus();
      
    } catch (err) {
      setError(err.message);
    }
  };

  const playAudio = async (audioUrl, audioId) => {
    try {
      // Stop any currently playing audio
      if (playingAudio) {
        playingAudio.pause();
      }
      
      // Play new audio
      const audio = new Audio(audioUrl);
      audio.play();
      setPlayingAudio(audio);
      
      audio.onended = () => setPlayingAudio(null);
      audio.onerror = () => setPlayingAudio(null);
      
    } catch (err) {
      setError('Failed to play audio');
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading workflow status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={fetchWorkflowStatus}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-4" />
          <p className="text-red-600">Article not found</p>
        </div>
      </div>
    );
  }

  const getScriptDisplayName = (scriptType) => {
    const names = {
      'short_script': 'Short Script (60-90s)',
      'long_script': 'Long Script (2-4min)', 
      'short_script_text': 'Short Script (Text Only)',
      'long_script_text': 'Long Script (Text Only)'
    };
    return names[scriptType] || scriptType;
  };

  const getScriptText = (scriptType) => {
    return article[scriptType] || '';
  };

  const hasAudio = (scriptType) => {
    return article.audio_tracks?.some(track => 
      track.script_type === scriptType && track.status === 'ready'
    );
  };

  const getAudioTrack = (scriptType) => {
    return article.audio_tracks?.find(track => 
      track.script_type === scriptType && track.status === 'ready'
    );
  };

  const isAudioApproved = (scriptType) => {
    return article.audio_tracks?.some(track => 
      track.script_type === scriptType && track.status === 'approved'
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{article.title}</h1>
              <p className="text-gray-600 mt-2">Workflow Hub - Audio Generation</p>
            </div>
            <button
              onClick={() => navigate('/articles')}
              className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Back to Articles
            </button>
          </div>
        </div>

        {/* Workflow Status */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Workflow Status</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className={`p-4 rounded-lg ${workflowStatus.article_processed ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
              <div className="flex items-center">
                <FileText className={`h-5 w-5 mr-2 ${workflowStatus.article_processed ? 'text-green-600' : 'text-gray-400'}`} />
                <span className="font-medium text-gray-900">Article</span>
              </div>
              <p className={`text-sm mt-1 ${workflowStatus.article_processed ? 'text-green-700' : 'text-gray-500'}`}>
                {workflowStatus.article_processed ? 'Processed' : 'Pending'}
              </p>
            </div>
            
            <div className={`p-4 rounded-lg ${workflowStatus.has_short_audio ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
              <div className="flex items-center">
                <Music className={`h-5 w-5 mr-2 ${workflowStatus.has_short_audio ? 'text-green-600' : 'text-gray-400'}`} />
                <span className="font-medium text-gray-900">Short Audio</span>
              </div>
              <p className={`text-sm mt-1 ${workflowStatus.has_short_audio ? 'text-green-700' : 'text-gray-500'}`}>
                {workflowStatus.has_short_audio ? 'Ready' : 'Not Generated'}
              </p>
            </div>
            
            <div className={`p-4 rounded-lg ${workflowStatus.has_long_audio ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
              <div className="flex items-center">
                <Music className={`h-5 w-5 mr-2 ${workflowStatus.has_long_audio ? 'text-green-600' : 'text-gray-400'}`} />
                <span className="font-medium text-gray-900">Long Audio</span>
              </div>
              <p className={`text-sm mt-1 ${workflowStatus.has_long_audio ? 'text-green-700' : 'text-gray-500'}`}>
                {workflowStatus.has_long_audio ? 'Ready' : 'Not Generated'}
              </p>
            </div>
            
            <div className={`p-4 rounded-lg ${workflowStatus.ready_for_video ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'}`}>
              <div className="flex items-center">
                <Video className={`h-5 w-5 mr-2 ${workflowStatus.ready_for_video ? 'text-blue-600' : 'text-gray-400'}`} />
                <span className="font-medium text-gray-900">Video Ready</span>
              </div>
              <p className={`text-sm mt-1 ${workflowStatus.ready_for_video ? 'text-blue-700' : 'text-gray-500'}`}>
                {workflowStatus.ready_for_video ? 'Yes' : 'No'}
              </p>
            </div>
          </div>
        </div>

        {/* Script Types */}
        <div className="space-y-6">
          {['short_script', 'long_script', 'short_script_text', 'long_script_text'].map((scriptType) => {
            const scriptText = getScriptText(scriptType);
            const audioExists = hasAudio(scriptType);
            const audioTrack = getAudioTrack(scriptType);
            const isApproved = isAudioApproved(scriptType);
            const isGenerating = generatingAudio[scriptType];
            
            if (!scriptText) return null;
            
            return (
              <div key={scriptType} className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {getScriptDisplayName(scriptType)}
                    </h3>
                    
                    <div className="flex items-center space-x-3">
                      {audioExists && audioTrack && (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => playAudio(audioTrack.file_url, audioTrack.id)}
                            className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                          >
                            {playingAudio?.src === audioTrack.file_url ? (
                              <Pause className="h-4 w-4 mr-1" />
                            ) : (
                              <Play className="h-4 w-4 mr-1" />
                            )}
                            Play
                          </button>
                          
                          <a
                            href={audioTrack.file_url}
                            download
                            className="flex items-center px-3 py-1 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700"
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Download
                          </a>
                          
                          {!isApproved && (
                            <button
                              onClick={() => approveAudio(audioTrack.id)}
                              className="flex items-center px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Approve
                            </button>
                          )}
                          
                          {isApproved && (
                            <span className="flex items-center px-3 py-1 text-sm bg-green-100 text-green-800 rounded-md">
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Approved
                            </span>
                          )}
                        </div>
                      )}
                      
                      {!audioExists && (
                        <button
                          onClick={() => generateAudio(scriptType)}
                          disabled={isGenerating}
                          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isGenerating ? (
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Music className="h-4 w-4 mr-2" />
                          )}
                          {isGenerating ? 'Generating...' : 'Generate Audio'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono">
                      {scriptText}
                    </pre>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Next Steps */}
        {workflowStatus.ready_for_video && (
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-center">
              <ArrowRight className="h-5 w-5 text-blue-600 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-blue-900">Ready for Video Generation</h3>
                <p className="text-blue-700 mt-1">
                  You have approved audio tracks ready for video generation with HeyGen.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default WorkflowHub;


